import { useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabaseClient';
import { calculateElo } from '../utils/elo';
import { sanitizeSearchQuery } from '../utils/sanitizeSearchQuery';
import FormField from './ui/FormField';
import Input from './ui/Input';
import CustomSelect from './ui/CustomSelect';
import Button from './ui/Button';
import RatingModal from './RatingModal';
import './ReportMatchModal.css';

/* ─────────────────────────────────────────────────────────────
   REPORT MATCH MODAL

   Props:
   - isOpen            (bool)   controls visibility
   - onClose           (fn)     called when user dismisses the modal
   - currentUserId     (string) excludes the logged-in user from search
   - onMatchSubmitted  (fn)     called after a match is saved
   ───────────────────────────────────────────────────────────── */

const WINS_NEEDED = { bo1: null, bo3: 2, bo5: 3, bo7: 4 };

function LockIcon() {
  return (
    <span className="score-lock-icon">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    </span>
  );
}

/* ── Reusable player search field ───────────────────────────── */
function PlayerSearch({ label, currentUserId, excludeIds = [], onSelect, placeholder = 'Search by name…', error }) {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [focused, setFocused]   = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase
        .from('profiles')
        .select('id, username, elo_rating, matches_played')
        .or(`full_name.ilike.%${sanitizeSearchQuery(query)}%,username.ilike.%${sanitizeSearchQuery(query)}%`)
        .neq('id', currentUserId)
        .limit(6);
      // Filter out already-chosen players
      setResults((data || []).filter(p => !excludeIds.includes(p.id)));
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  function handleSelect(player) {
    onSelect(player);
    setQuery('');
    setResults([]);
  }

  return (
    <FormField label={label} error={error}>
      <div className="opponent-search-wrap">
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          autoComplete="off"
          error={error}
        />
        {query.length >= 2 && (results.length > 0 || !searching) && (
          <div className="opponent-results">
            {results.length > 0 ? results.map(p => (
              <button key={p.id} type="button" className="opponent-result-item" onClick={() => handleSelect(p)}>
                <span>{p.username}</span>
                <span className="opponent-result-elo">{p.elo_rating}</span>
              </button>
            )) : (
              <p className="opponent-no-results">No players found.</p>
            )}
          </div>
        )}
      </div>
    </FormField>
  );
}

function ReportMatchModal({ isOpen, onClose, currentUserId, currentUserRating, currentUserMatches, onMatchSubmitted, editMatch, preSelectedOpponent }) {

  // Singles/Doubles toggle
  const [isDoubles, setIsDoubles] = useState(false);

  // Singles — opponent
  const [searchQuery, setSearchQuery]           = useState('');
  const [searchResults, setSearchResults]       = useState([]);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [searching, setSearching]               = useState(false);
  const [suggestions, setSuggestions]           = useState([]);
  const [inputFocused, setInputFocused]         = useState(false);

  // Doubles — partner + opp2
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedOpp2,    setSelectedOpp2]    = useState(null);

  // Match details (shared)
  const [result, setResult]               = useState('');
  const [myScore, setMyScore]             = useState('');
  const [oppScore, setOppScore]           = useState('');
  const [format, setFormat]               = useState('bo1');
  const [pointsPerGame, setPointsPerGame] = useState('11');

  const [errors, setErrors]           = useState({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isRated, setIsRated]         = useState(null);
  const [submitted, setSubmitted]     = useState(false);
  const searchRef                     = useRef(null);
  const lastAutoFill                  = useRef('');

  // ── Derived values ─────────────────────────────────────────
  const isBestOf   = format !== 'bo1';
  const winsNeeded = WINS_NEEDED[format];
  const winnerScore = isBestOf ? String(winsNeeded) : pointsPerGame;
  const loserMax   = isBestOf ? String(winsNeeded - 1) : String(Number(pointsPerGame) - 1);
  const myScoreLabel  = isBestOf ? 'Your games won'       : 'Your score';
  const oppScoreLabel = isBestOf ? "Opponent's games won" : "Opponent's score";
  const myScoreLocked  = isBestOf && result === 'win';
  const oppScoreLocked = isBestOf && result === 'loss';

  // ── Singles ELO preview ─────────────────────────────────────
  const eloPreview = (() => {
    if (isDoubles) return null;
    if (!selectedOpponent?.elo_rating || !result || !myScore || !oppScore ||
        currentUserRating == null || currentUserMatches == null) return null;
    const winnerRating  = result === 'win' ? currentUserRating           : selectedOpponent.elo_rating;
    const loserRating   = result === 'win' ? selectedOpponent.elo_rating : currentUserRating;
    const winnerMatches = result === 'win' ? currentUserMatches          : selectedOpponent.matches_played;
    const loserMatches  = result === 'win' ? selectedOpponent.matches_played : currentUserMatches;
    const winnerGames   = result === 'win' ? Number(myScore)  : Number(oppScore);
    const loserGames    = result === 'win' ? Number(oppScore) : Number(myScore);
    const { winnerChange, loserChange } = calculateElo({ winnerRating, loserRating, winnerMatches, loserMatches, format, winnerGames, loserGames });
    return result === 'win' ? winnerChange : loserChange;
  })();

  // ── Result toggle ───────────────────────────────────────────
  function handleResultChange(newResult) {
    if (newResult === result) return;
    if (myScore !== '' || oppScore !== '') {
      setMyScore(oppScore);
      setOppScore(myScore);
    }
    setResult(newResult);
  }

  // ── Auto-fill winner score ──────────────────────────────────
  useEffect(() => {
    if (!result) return;
    if (result === 'win') {
      if (isBestOf) setMyScore(winnerScore);
      else if (!myScore) setMyScore(winnerScore);
    } else {
      if (isBestOf) setOppScore(winnerScore);
      else if (!oppScore) setOppScore(winnerScore);
    }
  }, [result, format, pointsPerGame]);

  useEffect(() => {
    if (!isBestOf || !result) return;
    const max = winsNeeded - 1;
    if (result === 'win'  && oppScore !== '' && Number(oppScore) > max) setOppScore(String(max));
    if (result === 'loss' && myScore  !== '' && Number(myScore)  > max) setMyScore(String(max));
  }, [format]);

  // ── Pre-fill for edit mode ──────────────────────────────────
  useEffect(() => {
    if (!editMatch || !isOpen) return;
    const isWin    = editMatch.winner?.id === currentUserId;
    const opponent = isWin ? editMatch.loser : editMatch.winner;
    setSelectedOpponent({ id: opponent?.id || '', username: opponent?.username || '', elo_rating: opponent?.elo_rating || 1200, matches_played: opponent?.matches_played || 0 });
    setResult(isWin ? 'win' : 'loss');
    setFormat(editMatch.format || 'bo1');
    setMyScore(String(isWin ? editMatch.winner_games_won : editMatch.loser_games_won));
    setOppScore(String(isWin ? editMatch.loser_games_won : editMatch.winner_games_won));
    if (editMatch.points_per_game) setPointsPerGame(String(editMatch.points_per_game));
  }, [editMatch?.id, isOpen]);

  // ── Pre-select opponent ─────────────────────────────────────
  useEffect(() => {
    if (isOpen && preSelectedOpponent && !editMatch) setSelectedOpponent(preSelectedOpponent);
  }, [isOpen]);

  // ── ESC to close ────────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e) { if (e.key === 'Escape') handleClose(); }
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // ── Body scroll lock (iOS-safe) ──────────────────────────────
  // iOS Safari ignores overflow:hidden on body inside fixed modals.
  // Fixing the body position is the only reliable cross-device solution.
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top      = `-${scrollY}px`;
    document.body.style.width    = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top      = '';
      document.body.style.width    = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // ── Check rated status ──────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !currentUserId) return;
    setIsRated(null);
    supabase.from('profiles').select('is_rated').eq('id', currentUserId).single()
      .then(({ data }) => setIsRated(data?.is_rated ?? false));
  }, [isOpen, currentUserId]);

  // ── Suggestions (friends + recent) ─────────────────────────
  useEffect(() => {
    if (!isOpen || !currentUserId) return;
    async function fetchSuggestions() {
      const [{ data: asFriend }, { data: asAddressee }] = await Promise.all([
        supabase.from('friendships').select('friend_id').eq('user_id', currentUserId).eq('status', 'accepted'),
        supabase.from('friendships').select('user_id').eq('friend_id', currentUserId).eq('status', 'accepted'),
      ]);
      const friendIds = [...(asFriend || []).map(r => r.friend_id), ...(asAddressee || []).map(r => r.user_id)];
      let friends = [];
      if (friendIds.length > 0) {
        const { data } = await supabase.from('profiles').select('id, username, elo_rating, matches_played').in('id', friendIds);
        friends = (data || []).map(p => ({ ...p, _group: 'Friends' }));
      }
      const { data: recentMatches } = await supabase.from('matches')
        .select('winner:profiles!winner_id(id, username, elo_rating, matches_played), loser:profiles!loser_id(id, username, elo_rating, matches_played)')
        .or(`winner_id.eq.${currentUserId},loser_id.eq.${currentUserId}`)
        .eq('status', 'confirmed').order('played_at', { ascending: false }).limit(15);
      const seenIds = new Set([currentUserId, ...friendIds]);
      const recent = [];
      for (const m of (recentMatches || [])) {
        if (!m.winner || !m.loser) continue;
        const opp = m.winner.id === currentUserId ? m.loser : m.winner;
        if (!opp || seenIds.has(opp.id)) continue;
        seenIds.add(opp.id);
        recent.push({ ...opp, _group: 'Recent' });
        if (recent.length >= 5) break;
      }
      setSuggestions([...friends, ...recent]);
    }
    fetchSuggestions();
  }, [isOpen, currentUserId]);

  // ── Opponent search (singles) ───────────────────────────────
  useEffect(() => {
    if (searchQuery.trim().length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(() => searchOpponents(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  async function searchOpponents(query) {
    setSearching(true);
    const { data } = await supabase.from('profiles')
      .select('id, username, elo_rating, matches_played')
      .or(`full_name.ilike.%${sanitizeSearchQuery(query)}%,username.ilike.%${sanitizeSearchQuery(query)}%`)
      .neq('id', currentUserId).limit(6);
    setSearchResults(data || []);
    setSearching(false);
  }

  function selectOpponent(player) { setSelectedOpponent(player); setSearchQuery(''); setSearchResults([]); }
  function clearOpponent() { setSelectedOpponent(null); setSearchQuery(''); setTimeout(() => searchRef.current?.focus(), 50); }

  function handleClose() {
    setIsDoubles(false);
    setSearchQuery(''); setSearchResults([]); setSelectedOpponent(null); setSuggestions([]); setInputFocused(false);
    setSelectedPartner(null); setSelectedOpp2(null);
    setResult(''); setMyScore(''); setOppScore(''); setFormat('bo1'); setPointsPerGame('11');
    setErrors({}); setSubmitError(''); setSubmitting(false); setSubmitted(false);
    lastAutoFill.current = '';
    onClose();
  }

  function handleBackdropClick(e) { if (e.target === e.currentTarget) handleClose(); }

  // ── Submit ──────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');

    const finalMyScore  = myScore  !== '' ? myScore  : '0';
    const finalOppScore = oppScore !== '' ? oppScore : '0';

    // Sanitize scores — reject NaN, negative, or absurd values
    const parsedMy  = parseInt(finalMyScore, 10);
    const parsedOpp = parseInt(finalOppScore, 10);
    if (isNaN(parsedMy) || isNaN(parsedOpp) || parsedMy < 0 || parsedOpp < 0 || parsedMy > 99 || parsedOpp > 99) {
      setSubmitError('Scores must be numbers between 0 and 99.');
      return;
    }

    // Actual point totals from user's perspective
    const winnerPoints = result === 'win' ? parsedMy  : parsedOpp;
    const loserPoints  = result === 'win' ? parsedOpp : parsedMy;

    // RPC expects game counts, not point scores:
    //   BO1  → always 1–0 (one game played, winner takes it)
    //   BO3/5/7 → actual games won by each side (already a game count)
    const winnerGames = format === 'bo1' ? 1 : winnerPoints;
    const loserGames  = format === 'bo1' ? 0 : loserPoints;

    // ── Validate fields before any RPC calls ─────────────────
    if (isDoubles) {
      const newErrors = {};
      if (!selectedOpponent) newErrors.opp1    = 'Please select Opponent 1.';
      if (!selectedPartner)  newErrors.partner = 'Please select your partner.';
      if (!selectedOpp2)     newErrors.opp2    = 'Please select Opponent 2.';
      if (!result)           newErrors.result  = 'Please select a result.';
      if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    } else {
      const newErrors = {};
      if (!selectedOpponent) newErrors.opponent = 'Please select an opponent.';
      if (!result)           newErrors.result   = 'Please select a result.';
      if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    }

    setSubmitting(true);

    // ── Cancel old match FIRST when editing (prevents double-match) ──
    if (editMatch?.id) {
      const { error: cancelError } = await supabase.rpc('cancel_match', { p_match_id: editMatch.id });
      if (cancelError) {
        setSubmitting(false);
        setSubmitError('Could not cancel the original match. Please try again.');
        return;
      }
    }

    // ── Create replacement match ─────────────────────────────
    if (isDoubles) {
      const { error } = await supabase.rpc('report_doubles_match', {
        p_partner_id:      selectedPartner.id,
        p_opp1_id:         selectedOpponent.id,
        p_opp2_id:         selectedOpp2.id,
        p_reporter_won:    result === 'win',
        p_winner_games:    winnerGames,
        p_loser_games:     loserGames,
        p_format:          format,
        p_points_per_game: Number(pointsPerGame),
      });
      if (error) {
        setSubmitting(false);
        setSubmitError(error.message || 'Could not save match. Please try again.');
        return;
      }
    } else {
      const winnerId   = result === 'win' ? currentUserId : selectedOpponent.id;
      const loserId    = result === 'win' ? selectedOpponent.id : currentUserId;
      // For BO1, game_scores stores the actual point score; winnerGames/loserGames are already 1/0
      const gameScores = format === 'bo1' ? JSON.stringify([{ w: winnerPoints, l: loserPoints }]) : null;

      const { error } = await supabase.rpc('report_match', {
        p_winner_id: winnerId, p_loser_id: loserId,
        p_winner_games: winnerGames, p_loser_games: loserGames,
        p_format: format, p_game_scores: gameScores,
      });
      if (error) {
        setSubmitting(false);
        setSubmitError(error.message || 'Could not save match. Please try again.');
        return;
      }
    }

    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => { onMatchSubmitted?.(); handleClose(); }, 2500);
  }

  if (!isOpen) return null;

  if (isRated === false) {
    return <RatingModal isOpen={true} onClose={handleClose} userId={currentUserId} onRated={() => setIsRated(true)} />;
  }

  // IDs already chosen in doubles mode (for excluding from searches)
  const chosenIds = [
    currentUserId,
    selectedOpponent?.id,
    selectedPartner?.id,
    selectedOpp2?.id,
  ].filter(Boolean);

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-card">

        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">{editMatch ? 'Edit Match Request' : 'Report a Match'}</h2>
          <button className="modal-close" onClick={handleClose} aria-label="Close">&times;</button>
        </div>

        {/* ── Success screen ───────────────────────────────────── */}
        {submitted && (
          <div className="modal-success">
            <span className="modal-success-icon">✓</span>
            <p className="modal-success-title">Match request sent!</p>
            <p className="modal-success-sub">
              {isDoubles
                ? "Waiting for all 3 players to confirm. Ratings won't update until everyone accepts."
                : <>Waiting for <strong>{selectedOpponent?.username}</strong> to confirm. Your rating won&apos;t change until they accept.</>
              }
            </p>
          </div>
        )}

        <form className="modal-form" onSubmit={handleSubmit} noValidate style={submitted ? { display: 'none' } : undefined}>

          {/* ── Singles / Doubles toggle ─────────────────────── */}
          {!editMatch && (
            <div className="match-type-toggle">
              <button
                type="button"
                className={`match-type-btn ${!isDoubles ? 'match-type-btn--active' : ''}`}
                onClick={() => { setIsDoubles(false); setSelectedPartner(null); setSelectedOpp2(null); setErrors({}); }}
              >
                Singles
              </button>
              <button
                type="button"
                className={`match-type-btn ${isDoubles ? 'match-type-btn--active' : ''}`}
                onClick={() => { setIsDoubles(true); setErrors({}); }}
              >
                Doubles
              </button>
            </div>
          )}

          {/* ── Doubles: Partner ────────────────────────────── */}
          {isDoubles && (
            <>
              {selectedPartner ? (
                <FormField label="Your Partner">
                  <div className="opponent-selected">
                    <span>{selectedPartner.username}</span>
                    <button type="button" className="opponent-clear" onClick={() => setSelectedPartner(null)}>&times;</button>
                  </div>
                </FormField>
              ) : (
                <PlayerSearch
                  label="Your Partner"
                  currentUserId={currentUserId}
                  excludeIds={chosenIds}
                  onSelect={p => setSelectedPartner(p)}
                  placeholder="Search partner…"
                  error={errors.partner}
                />
              )}
            </>
          )}

          {/* ── Opponent 1 (or "Opponent" in singles) ────────── */}
          <FormField label={isDoubles ? 'Opponent 1' : 'Opponent'} error={errors.opponent || errors.opp1}>
            {selectedOpponent ? (
              <div className="opponent-selected">
                <span>{selectedOpponent.username}</span>
                <button type="button" className="opponent-clear" onClick={clearOpponent} aria-label="Clear opponent">&times;</button>
              </div>
            ) : (
              <div className="opponent-search-wrap">
                <Input
                  id="opponent-search"
                  ref={searchRef}
                  type="text"
                  placeholder="Search by name…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setTimeout(() => setInputFocused(false), 150)}
                  autoComplete="off"
                  error={errors.opponent || errors.opp1}
                />
                {inputFocused && searchQuery === '' && suggestions.length > 0 && (
                  <div className="opponent-results">
                    {['Friends', 'Recent'].map(group => {
                      const items = suggestions.filter(s => s._group === group && !chosenIds.includes(s.id));
                      if (!items.length) return null;
                      return (
                        <div key={group}>
                          <p className="opponent-suggestion-group">{group === 'Recent' ? 'Recent Opponents' : 'Friends'}</p>
                          {items.map(player => (
                            <button key={player.id} type="button" className="opponent-result-item" onClick={() => selectOpponent(player)}>
                              <span>{player.username}</span>
                              <span className="opponent-result-elo">{player.elo_rating}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
                {searchQuery.length >= 2 && (searchResults.length > 0 || !searching) && (
                  <div className="opponent-results">
                    {searchResults.filter(p => !chosenIds.includes(p.id)).length > 0 ? (
                      searchResults.filter(p => !chosenIds.includes(p.id)).map(player => (
                        <button key={player.id} type="button" className="opponent-result-item" onClick={() => selectOpponent(player)}>
                          <span>{player.username}</span>
                          <span className="opponent-result-elo">{player.elo_rating}</span>
                        </button>
                      ))
                    ) : (
                      <p className="opponent-no-results">No players found.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </FormField>

          {/* ── Doubles: Opponent 2 ──────────────────────────── */}
          {isDoubles && (
            <>
              {selectedOpp2 ? (
                <FormField label="Opponent 2">
                  <div className="opponent-selected">
                    <span>{selectedOpp2.username}</span>
                    <button type="button" className="opponent-clear" onClick={() => setSelectedOpp2(null)}>&times;</button>
                  </div>
                </FormField>
              ) : (
                <PlayerSearch
                  label="Opponent 2"
                  currentUserId={currentUserId}
                  excludeIds={chosenIds}
                  onSelect={p => setSelectedOpp2(p)}
                  placeholder="Search by name…"
                  error={errors.opp2}
                />
              )}
            </>
          )}

          {/* ── Format + Points per game ─────────────────────── */}
          <div className="modal-format-row">
            <FormField label="Format">
              <CustomSelect value={format} onChange={v => setFormat(v)} options={[
                { value: 'bo1', label: 'Single game' },
                { value: 'bo3', label: 'Best of 3'   },
                { value: 'bo5', label: 'Best of 5'   },
                { value: 'bo7', label: 'Best of 7'   },
              ]} />
            </FormField>
            <FormField label="Points per game">
              <CustomSelect value={pointsPerGame} onChange={v => setPointsPerGame(v)} options={[
                { value: '11', label: 'First to 11' },
                { value: '21', label: 'First to 21' },
              ]} />
            </FormField>
          </div>

          {/* ── Win / Loss toggle ────────────────────────────── */}
          <FormField label="Result" error={errors.result}>
            <div className="result-toggle-row">
              <button type="button" className={`result-toggle-btn ${result === 'win' ? 'selected-win' : ''}`} onClick={() => handleResultChange('win')}>
                {isDoubles ? 'We Won' : 'I Won'}
              </button>
              <button type="button" className={`result-toggle-btn ${result === 'loss' ? 'selected-loss' : ''}`} onClick={() => handleResultChange('loss')}>
                {isDoubles ? 'We Lost' : 'I Lost'}
              </button>
            </div>
          </FormField>

          {/* ── Score inputs ─────────────────────────────────── */}
          <div className="score-row">
            <FormField label={myScoreLabel} error={errors.myScore}>
              <div className="score-input-wrap">
                <Input id="my-score" type="number" placeholder="0" value={myScore} onChange={e => setMyScore(e.target.value)}
                  min="0" max={myScoreLocked ? winnerScore : loserMax} readOnly={myScoreLocked} error={errors.myScore} />
                {myScoreLocked && <LockIcon />}
              </div>
            </FormField>
            <span className="score-dash">—</span>
            <FormField label={oppScoreLabel} error={errors.oppScore}>
              <div className="score-input-wrap">
                <Input id="opp-score" type="number" placeholder="0" value={oppScore} onChange={e => setOppScore(e.target.value)}
                  min="0" max={oppScoreLocked ? winnerScore : loserMax} readOnly={oppScoreLocked} error={errors.oppScore} />
                {oppScoreLocked && <LockIcon />}
              </div>
            </FormField>
          </div>

          {/* ── Singles ELO preview ──────────────────────────── */}
          {!isDoubles && eloPreview != null && (
            <p className={`elo-preview ${eloPreview >= 0 ? 'elo-preview--gain' : 'elo-preview--loss'}`}>
              Estimated rating change: <strong>{eloPreview >= 0 ? `+${eloPreview}` : eloPreview} pts</strong>
            </p>
          )}

          {submitError && <p className="auth-error">{submitError}</p>}

          <div className="modal-footer">
            <Button type="submit" fullWidth size="lg" disabled={submitting}>
              {submitting ? 'Saving…' : 'Submit Match'}
            </Button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default ReportMatchModal;
